import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";

export const fetchGoogleFontData = createAsyncThunk(
  "loadgooglefont",
  async (formData: FormData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/loadgooglefont`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${import.meta.env.VITE_API_KEY}`,
          },
        },
      );

      return response.data;
    } catch (error) {
      throw rejectWithValue((error as AxiosError)?.response?.data);
    }
  },
);

export const fetchTextColorData = createAsyncThunk(
  "getTextColor",
  async (formData: FormData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/getTextColor`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${import.meta.env.VITE_API_KEY}`,
          },
        },
      );
      return response.data.textColor;
    } catch (error) {
      throw rejectWithValue((error as AxiosError)?.response?.data);
    }
  },
);

export const fetchTextOutlineColorData = createAsyncThunk(
  "getTextOutlineColor",
  async (formData: FormData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/getTextOutlineColor`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${import.meta.env.VITE_API_KEY}`,
          },
        },
      );
      return response.data.textColor;
    } catch (error) {
      throw rejectWithValue((error as AxiosError)?.response?.data);
    }
  },
);

export const createTexttoImageData = createAsyncThunk(
  "createTexttoImage",
  async (formData: FormData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/createTexttoImage`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${import.meta.env.VITE_API_KEY}`,
          },
        },
      );
      return response.data;
    } catch (error) {
      throw rejectWithValue((error as AxiosError)?.response?.data);
    }
  },
);

interface TextState {
  googleFontsLoading: boolean;
  textColorsLoading: boolean;
  textOutlineColorsLoading: boolean;
  createTexttoImageLoading: boolean;
  data: {
    googleFonts: any;
    textColors: any;
    textOutlineColors: any;
  };
  error: string;
}

const initialState: TextState = {
  googleFontsLoading: false,
  textColorsLoading: false,
  textOutlineColorsLoading: false,
  createTexttoImageLoading: false,
  data: {
    googleFonts: null,
    textColors: null,
    textOutlineColors: null,
  },
  error: "",
};

export const textSlice = createSlice({
  name: "text",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchGoogleFontData.pending, (state) => {
        state.googleFontsLoading = true;
        state.error = "";
      })
      .addCase(fetchGoogleFontData.fulfilled, (state, action) => {
        state.googleFontsLoading = false;
        state.data.googleFonts = action.payload;
        state.error = "";
      })
      .addCase(fetchGoogleFontData.rejected, (state, action) => {
        state.googleFontsLoading = false;
        state.data.googleFonts = null;
        state.error =
          action.error.message || "Failed to fetch google font data";
      });
    builder
      .addCase(fetchTextColorData.pending, (state) => {
        state.textColorsLoading = true;
        state.error = "";
      })
      .addCase(fetchTextColorData.fulfilled, (state, action) => {
        state.textColorsLoading = false;
        state.data.textColors = action.payload;
        state.error = "";
      })
      .addCase(fetchTextColorData.rejected, (state, action) => {
        state.textColorsLoading = false;
        state.data.textColors = null;
        state.error = action.error.message || "Failed to fetch text color data";
      });
    builder
      .addCase(fetchTextOutlineColorData.pending, (state) => {
        state.textOutlineColorsLoading = true;
        state.error = "";
      })
      .addCase(fetchTextOutlineColorData.fulfilled, (state, action) => {
        state.textOutlineColorsLoading = false;
        state.data.textOutlineColors = action.payload;
        state.error = "";
      })
      .addCase(fetchTextOutlineColorData.rejected, (state, action) => {
        state.textOutlineColorsLoading = false;
        state.data.textOutlineColors = null;
        state.error =
          action.error.message || "Failed to fetch text outline color data";
      });
    builder
      .addCase(createTexttoImageData.pending, (state) => {
        state.createTexttoImageLoading = true;
        state.error = "";
      })
      .addCase(createTexttoImageData.fulfilled, (state) => {
        state.createTexttoImageLoading = false;
        state.error = "";
      })
      .addCase(createTexttoImageData.rejected, (state, action) => {
        state.createTexttoImageLoading = false;
        state.error = action.error.message || "Something went wrong";
      });
  },
});

export default textSlice.reducer;
